import { getUiById } from './ui.js';

/**
 * Converts markdown format with tool annotations back to HTML for TipTap editor
 * Handles conversion of %{Tool:...} format to item spans
 */
export class MarkdownToHtmlConverter {
  static EVENT_ATTRIBUTE_LABELS = {
    pax: 'Guests',
    date: 'Date',
    system_stage: 'Stage',
  };

  constructor() {
    // DOM parser for creating HTML elements
    this.parser = new DOMParser();
  }

  /**
   * Convert markdown content with tool annotations to HTML for TipTap editor
   * @param {string} markdown - The markdown content with tool annotations
   * @param {Object} store - Vuex store for accessing attribute display names
   * @returns {string} - HTML content suitable for TipTap editor
   */
  static convertToHtml(markdown, store = null) {
    if (!markdown || typeof markdown !== 'string') {
      return '<p></p>';
    }

    // First, convert tool annotations to temporary placeholders
    let html =
      MarkdownToHtmlConverter.convertToolAnnotationsToPlaceholders(markdown);

    // Then convert basic markdown to HTML
    html = MarkdownToHtmlConverter.convertBasicMarkdownToHtml(html);

    // Finally, convert placeholders to proper item spans
    html = MarkdownToHtmlConverter.convertPlaceholdersToItemSpans(html, store);

    // Ensure we have at least one paragraph
    if (!html.trim()) {
      return '<p></p>';
    }

    return html;
  }

  /**
   * Convert %{Tool:...}, %{Attribute:...}, and %{Library:...} annotations and [content](tooltip) to temporary placeholders
   * @param {string} markdown - Markdown content with tool annotations
   * @returns {string} - Content with placeholders
   */
  static convertToolAnnotationsToPlaceholders(markdown) {
    let result = markdown;

    // Match tool annotations: %{Tool:toolId}
    result = result.replace(/%\{Tool:([a-zA-Z0-9_-]+)\}/g, (match, toolId) => {
      return `__TOOL_PLACEHOLDER_TOOL_${toolId}__`;
    });

    // Match attribute annotations: %{Attribute:attributeId}
    result = result.replace(
      /%\{Attribute:([a-zA-Z0-9_-]+)\}/g,
      (match, attributeId) => {
        return `__TOOL_PLACEHOLDER_ATTR_${attributeId}__`;
      }
    );

    // Match contact attribute annotations: %{ContactAttribute:contactAttributeId}
    result = result.replace(
      /%\{ContactAttribute:([a-zA-Z0-9_-]+)\}/g,
      (match, contactAttributeId) => {
        return `__ATTRIBUTE_CONTACT_PLACEHOLDER_${contactAttributeId}__`;
      }
    );

    // Match event attribute annotations: %{EventAttribute:key} or legacy %{EventField:key}
    result = result.replace(
      /%\{(?:EventAttribute|EventField):([a-zA-Z0-9_-]+)\}/g,
      (match, eventAttributeKey) => {
        return `__EVENT_ATTRIBUTE_PLACEHOLDER_${eventAttributeKey}__`;
      }
    );

    // Match library annotations: %{Library:namespace}
    result = result.replace(
      /%\{Library:([a-zA-Z0-9_-]+)\}/g,
      (match, namespace) => {
        return `__TOOL_PLACEHOLDER_LIB_${namespace}__`;
      }
    );

    // Match visualization annotations: %{Visualization:vizId}
    result = result.replace(
      /%\{Visualization:([a-zA-Z0-9_-]+)\}/g,
      (match, vizId) => {
        return `__TOOL_PLACEHOLDER_VIZ_${vizId}__`;
      }
    );

    // Match attribute pill syntax: [content](tooltip) or [content]
    result = result.replace(
      /\[([^\]]+)\](?:\(([^)]*)\))?/g,
      (match, content, tooltip) => {
        const tooltipPart = tooltip ? `_TOOLTIP_${tooltip}` : '';
        return `__ATTRIBUTE_PLACEHOLDER_${content}${tooltipPart}__`;
      }
    );

    return result;
  }

  /**
   * Check if a line looks like a markdown table row (starts and ends with |)
   * @param {string} line - Single line
   * @returns {boolean}
   */
  static isTableRow(line) {
    const trimmed = line.trim();
    return (
      trimmed.length > 0 && trimmed.startsWith('|') && trimmed.endsWith('|')
    );
  }

  /**
   * Check if a line is a markdown table separator (e.g. | --- | --- |)
   * @param {string} line - Single line
   * @returns {boolean}
   */
  static isTableSeparator(line) {
    return /^\|\s*[-:]+\s*(\|\s*[-:]+\s*)+\|$/.test(line.trim());
  }

  /**
   * Parse a markdown table block into HTML table
   * @param {string} block - Block of lines (table rows)
   * @returns {string} - HTML table or original block if not a valid table
   */
  static markdownTableToHtml(block) {
    const lines = block
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean);
    if (lines.length < 2 || !lines.every(MarkdownToHtmlConverter.isTableRow)) {
      return block;
    }

    const rows = [];
    let i = 0;
    if (MarkdownToHtmlConverter.isTableSeparator(lines[1])) {
      rows.push({
        type: 'header',
        cells: MarkdownToHtmlConverter.parseTableRow(lines[0]),
      });
      i = 2;
    } else {
      rows.push({
        type: 'header',
        cells: MarkdownToHtmlConverter.parseTableRow(lines[0]),
      });
      i = 1;
    }
    for (; i < lines.length; i += 1) {
      rows.push({
        type: 'body',
        cells: MarkdownToHtmlConverter.parseTableRow(lines[i]),
      });
    }

    const theadCells = rows[0].cells.map(c => `<th>${c}</th>`).join('');
    const thead = `<thead><tr>${theadCells}</tr></thead>`;
    const bodyRows = rows
      .slice(1)
      .map(r => `<tr>${r.cells.map(c => `<td>${c}</td>`).join('')}</tr>`)
      .join('');
    const tbody = bodyRows ? `<tbody>${bodyRows}</tbody>` : '';

    return `<table>${thead}${tbody}</table>`;
  }

  /**
   * Parse a single markdown table row into cell contents
   * @param {string} line - Line like "| A | B | C |"
   * @returns {string[]} - Array of cell content strings
   */
  static parseTableRow(line) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return [];
    const inner = trimmed.slice(1, -1);
    return inner.split('|').map(cell => cell.trim());
  }

  /**
   * Convert basic markdown syntax to HTML
   * @param {string} content - Content with placeholders
   * @returns {string} - HTML content
   */
  static convertBasicMarkdownToHtml(content) {
    let html = content;

    // Convert markdown table blocks to HTML before other conversions
    const blocks = html.split(/\n\s*\n/);
    html = blocks
      .map(block => {
        const trimmed = block.trim();
        const lines = trimmed
          .split('\n')
          .map(l => l.trim())
          .filter(Boolean);
        const isTable =
          lines.length >= 1 && lines.every(MarkdownToHtmlConverter.isTableRow);
        if (isTable) {
          return MarkdownToHtmlConverter.markdownTableToHtml(trimmed);
        }
        return block;
      })
      .join('\n\n');

    // Convert headers
    html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

    // Convert bold and italic
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Convert inline code
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Convert links
    html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>');

    // Convert images
    html = html.replace(/!\[(.+?)\]\((.+?)\)/g, '<img src="$2" alt="$1" />');

    // Convert unordered lists
    html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

    // Convert line breaks to paragraphs
    const paragraphs = html.split(/\n\s*\n/).filter(p => p.trim());
    if (paragraphs.length === 0) {
      return '<p></p>';
    }

    html = paragraphs
      .map(paragraph => {
        const trimmed = paragraph.trim();
        // Don't wrap headings, lists, tables, or already wrapped content in paragraphs
        if (
          trimmed.startsWith('<h') ||
          trimmed.startsWith('<ul>') ||
          trimmed.startsWith('<ol>') ||
          trimmed.startsWith('<li>') ||
          trimmed.startsWith('<p>') ||
          trimmed.startsWith('<table>')
        ) {
          return trimmed;
        }
        return `<p>${trimmed}</p>`;
      })
      .join('');

    return html;
  }

  /**
   * Convert placeholders back to proper item spans
   * @param {string} html - HTML content with placeholders
   * @param {Object} store - Vuex store for accessing attribute display names
   * @returns {string} - HTML with proper item spans
   */
  static convertPlaceholdersToItemSpans(html, store = null) {
    // Convert tool placeholders
    html = html.replace(
      /__TOOL_PLACEHOLDER_TOOL_([a-zA-Z0-9_-]+)__/g,
      (match, toolId) => {
        return MarkdownToHtmlConverter.createToolSpan(toolId, 'tool', store);
      }
    );

    // Convert attribute placeholders
    html = html.replace(
      /__TOOL_PLACEHOLDER_ATTR_([a-zA-Z0-9_-]+)__/g,
      (match, attributeId) => {
        return MarkdownToHtmlConverter.createToolSpan(
          attributeId,
          'attribute',
          store
        );
      }
    );

    // Convert contact attribute placeholders
    html = html.replace(
      /__ATTRIBUTE_CONTACT_PLACEHOLDER_([a-zA-Z0-9_-]+)__/g,
      (match, contactAttributeId) => {
        return MarkdownToHtmlConverter.createToolSpan(
          contactAttributeId,
          'contact_attribute',
          store
        );
      }
    );

    // Convert event attribute placeholders
    html = html.replace(
      /__EVENT_ATTRIBUTE_PLACEHOLDER_([a-zA-Z0-9_-]+)__/g,
      (match, eventAttributeKey) => {
        return MarkdownToHtmlConverter.createToolSpan(
          eventAttributeKey,
          'event_attribute',
          store
        );
      }
    );

    // Convert library placeholders
    html = html.replace(
      /__TOOL_PLACEHOLDER_LIB_([a-zA-Z0-9_-]+)__/g,
      (match, namespace) => {
        return MarkdownToHtmlConverter.createToolSpan(
          namespace,
          'library',
          store
        );
      }
    );

    // Convert visualization placeholders
    html = html.replace(
      /__TOOL_PLACEHOLDER_VIZ_([a-zA-Z0-9_-]+)__/g,
      (match, vizId) => {
        return MarkdownToHtmlConverter.createToolSpan(
          vizId,
          'visualization',
          store
        );
      }
    );

    // Convert attribute pill placeholders
    html = html.replace(
      /__ATTRIBUTE_PLACEHOLDER_([^_]+)(?:_TOOLTIP_([^_]*))?__/g,
      (match, content, tooltip) => {
        return MarkdownToHtmlConverter.createAttributePillSpan(
          content,
          tooltip
        );
      }
    );

    return html;
  }

  /**
   * Create an attribute pill span element
   * @param {string} content - The attribute content
   * @param {string} tooltip - Optional tooltip text
   * @returns {string} - HTML span element
   */
  static createAttributePillSpan(content, tooltip) {
    const attributes = [
      'data-type="attribute"',
      'class="attribute-pill"',
      `data-content="${content}"`,
    ];

    if (tooltip) {
      attributes.push(`data-tooltip="${tooltip}"`);
      attributes.push(`title="${tooltip}"`);
    }

    return `<span ${attributes.join(' ')}>${content}</span>`;
  }

  /**
   * Create an item span element for tools/attributes
   * @param {string} id - The tool/attribute ID
   * @param {string} type - 'tool' or 'attribute'
   * @param {Object} store - Vuex store for accessing attribute display names
   * @returns {string} - HTML span element
   */
  static createToolSpan(id, type, store = null) {
    const baseAttributes = [
      'data-type="item"',
      `data-item-type="${type}"`,
      'class="item-reference"',
      `data-id="${id}"`,
    ];

    let displayType = 'Tool';
    if (type === 'attribute') {
      displayType = 'Attr';
    } else if (type === 'contact_attribute') {
      displayType = 'Attr';
    } else if (type === 'event_attribute') {
      displayType = 'Attr';
    } else if (type === 'library') {
      displayType = 'Library';
    } else if (type === 'visualization') {
      displayType = 'UI';
    }

    // Get the display name and icon from the store if available
    let displayName = MarkdownToHtmlConverter.formatDisplayName(id);
    let icon = null;

    if (type === 'event_attribute') {
      const builtinLabel = MarkdownToHtmlConverter.EVENT_ATTRIBUTE_LABELS[id];
      if (builtinLabel) {
        displayName = builtinLabel;
        icon = 'i-lucide-calendar';
      }
    }

    if (
      store &&
      (type === 'attribute' ||
        type === 'contact_attribute' ||
        type === 'event_attribute')
    ) {
      try {
        const attributeFromStore =
          store.getters['attributes/getAttributeByKey']?.(id);
        if (attributeFromStore) {
          if (attributeFromStore.attribute_display_name) {
            displayName = attributeFromStore.attribute_display_name;
          }
          // Check if attribute has an icon (for future enhancement)
          if (
            attributeFromStore.icon &&
            attributeFromStore.icon.startsWith('i-lucide-')
          ) {
            icon = attributeFromStore.icon;
          }
        }
      } catch (error) {
        // Keep the formatted or builtin name if store access fails
      }
    } else if (store && type === 'tool') {
      try {
        const toolFromStore = store.getters['tools/getToolByName']?.(id);
        if (toolFromStore) {
          const meta = toolFromStore.meta || toolFromStore.metadata;
          const metaName =
            meta && typeof meta === 'object'
              ? (meta.name ?? meta.Name ?? null)
              : null;
          const baseName =
            metaName ||
            toolFromStore.label ||
            toolFromStore.title ||
            toolFromStore.name;
          const integration =
            meta && typeof meta === 'object'
              ? (meta.integration ?? meta.Integration ?? null)
              : null;
          const integrationLabel =
            integration &&
            String(integration).charAt(0).toUpperCase() +
              String(integration).slice(1).toLowerCase();
          displayName = integrationLabel
            ? `${integrationLabel} » ${baseName}`
            : baseName;
          if (toolFromStore.icon) {
            icon = toolFromStore.icon;
          }
        }
      } catch (error) {
        // Fallback to formatted name if store access fails
        displayName = MarkdownToHtmlConverter.formatDisplayName(id);
      }
    } else if (store && type === 'library') {
      try {
        const knowledgebase = store.getters['knowledgebase/getLibraries'].find(
          kb => kb.namespace === id
        );
        if (knowledgebase && knowledgebase.name) {
          displayName = knowledgebase.name;
          // Get library type icon based on type
          const getLibraryTypeIcon = libraryType => {
            const iconMap = {
              catalog: 'i-lucide-layout-grid',
              gallery: 'i-lucide-book-image',
              documents: 'i-lucide-file',
            };
            return iconMap[libraryType] || 'i-lucide-file';
          };
          // Check if the knowledgebase has a custom icon, otherwise use type-based icon
          if (
            knowledgebase.icon &&
            knowledgebase.icon.startsWith('i-lucide-')
          ) {
            icon = knowledgebase.icon;
          } else {
            icon = getLibraryTypeIcon(knowledgebase.type);
          }
        }
        // Add namespace data attribute for libraries
        baseAttributes.push(`data-namespace="${id}"`);
      } catch (error) {
        // Fallback to formatted name if store access fails
        displayName = MarkdownToHtmlConverter.formatDisplayName(id);
        icon = 'i-lucide-file'; // Default library icon
      }
    } else if (type === 'visualization') {
      // For visualizations, get the actual configuration from ui.js
      try {
        const uiConfig = getUiById(id);
        if (uiConfig) {
          displayName = uiConfig.label || uiConfig.name || uiConfig.title;
          icon = uiConfig.icon;
        } else {
          displayName = MarkdownToHtmlConverter.formatDisplayName(id);
          icon = 'i-lucide-layout-dashboard';
        }
      } catch (error) {
        displayName = MarkdownToHtmlConverter.formatDisplayName(id);
        icon = 'i-lucide-layout-dashboard';
      }
      // Add visualization-specific data attribute
      baseAttributes.push(`data-visualization-id="${id}"`);
    }

    // Add data-label attribute to preserve the display name
    baseAttributes.push(`data-label="${displayName}"`);

    // Add data-icon attribute if icon is available
    if (icon) {
      baseAttributes.push(`data-icon="${icon}"`);
    }

    const attributes = baseAttributes.join(' ');

    // Create icon element if icon is available
    const iconElement =
      icon && icon.startsWith('i-lucide-')
        ? `<span class="${icon} w-3 h-3 inline-block mr-1"></span>`
        : '';

    return (
      `<span ${attributes}>` +
      `<span class="item-type">${displayType}</span>` +
      `<span class="item-action">${iconElement}${displayName}</span>` +
      `</span>`
    );
  }

  /**
   * Format the display name for tools/attributes
   * @param {string} id - The ID to format
   * @returns {string} - Formatted display name
   */
  static formatDisplayName(id) {
    // Convert snake_case or kebab-case to readable format
    return id.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Unescape markdown characters that were escaped during HTML to markdown conversion
   * @param {string} text - Text with escaped characters
   * @returns {string} - Unescaped text
   */
  static unescapeMarkdownCharacters(text) {
    if (!text) return '';

    return text
      .replace(/\\\\/g, '\\')
      .replace(/\\\[/g, '[')
      .replace(/\\\]/g, ']')
      .replace(/\\`/g, '`');
  }

  /**
   * Validate that the generated HTML is valid for TipTap
   * @param {string} html - HTML to validate
   * @returns {boolean} - Whether the HTML is valid
   */
  validateHtml(html) {
    try {
      const doc = this.parser.parseFromString(html, 'text/html');
      return !doc.querySelector('parsererror');
    } catch (error) {
      return false;
    }
  }

  /**
   * Clean up HTML content to ensure compatibility with TipTap
   * @param {string} html - HTML content to clean
   * @returns {string} - Cleaned HTML content
   */
  static cleanHtmlForTipTap(html) {
    // Remove any potentially problematic attributes or elements
    let cleaned = html;

    // Ensure all paragraphs are properly closed
    cleaned = cleaned.replace(
      /<p>([^<]*(?:<(?!\/?p)[^<]*)*)<\/p>/g,
      '<p>$1</p>'
    );

    // Remove any stray closing tags without opening tags
    cleaned = cleaned.replace(/<\/p>(?![^<]*<p)/g, '');

    // Ensure we have at least one paragraph if content exists
    if (cleaned.trim() && !cleaned.includes('<p>') && !cleaned.includes('<h')) {
      cleaned = `<p>${cleaned}</p>`;
    }

    return cleaned;
  }
}

// Export the class with static methods
export default MarkdownToHtmlConverter;
